import React from 'react';
import { IReceiptData } from '../../../ApiCalls';
import DateTimeInput from './Forms/DateTimeInput';
import ProgressBar from './Forms/ProgressBar';
import TextInput from './Forms/TextInput';
import CurrencyInput from './Forms/CurrencyInput';
import SelectInput from './Forms/SelectInput';
import { GlobalVariables } from '../../../GlobalVariables';
import VerticalStretch from './Forms/VerticalStretch';
import { ReduxUpdater } from '../../../redux/ReduxUpdater';
import { useSelector } from 'react-redux';
import { checkReceiptData } from '../Helpers';
import { documentLoadingErrorState, formUpdatingState } from '../../../redux/slices/statusesSlice';
import { NavSpinner } from './Forms/NavSpinner';
import NavButtonsGroup from './Forms/NavButtonsGroup';
import ConfirmAndNextAdminMenu from './AdminMenu';
import { FilterCategories } from '../../../Shared';
import { GlobalPermissions } from '../../../GlobalPermissions';

export interface IConfirmAndNextReimbursementFormProps {
    idx: number;
    max: number;
    receipt: IReceiptData;
    receiptChanges: IReceiptData;
    onConfirm: () => void;
    onSave: () => void;
    onPrev: () => void;
    onNext: () => void;
}

const ConfirmAndNextReimbursementForm = (props: IConfirmAndNextReimbursementFormProps) => {
    const currentImageLoadingErrorState = useSelector(documentLoadingErrorState);
    const isFormUpdating = useSelector(formUpdatingState);
    
    const { idx, max, receipt, receiptChanges, onConfirm, onPrev, onNext, onSave } = props;

    const [doFilter, setDoFilter] = React.useState(true);

    console.log('ConfirmAndNextReimbursementForm:', receipt, receiptChanges);

    const date = receiptChanges.transaction_date ?? receipt.transaction_date;
    // const TS = Date.now();
    const ID = ''; //`${receipt.id}-${TS}`;
    const mergedData = {
        ...receipt,
        ...receiptChanges,
    };

    const handleConfirm = () => {
        if (checkReceiptData(mergedData, GlobalVariables.coOwnersExtendedArray, currentImageLoadingErrorState)) {
            onConfirm();
        }
    }

    const handleSave = () => {
        setTimeout(() => {
            onSave();
        }, 250);
    }

    const handleRevert = () => {
        ReduxUpdater.receiptChanges = {} as IReceiptData;
    }

    GlobalVariables.allowReceiptChanges = false;
    React.useEffect(() => {
        setTimeout(() => {
            GlobalVariables.allowReceiptChanges = true;
        }, 250);
    });

    const userCanReimburseReceipts = GlobalPermissions.UX.EXPENSES.ReviewAndConfirm.CanReimburseReceipts.includes(ReduxUpdater.authenticatedUser?.user_role ?? '');
    const showAll = receipt.user_description && receipt.user_description.toLowerCase().indexOf('food') !== -1;
    const filteredCategories = React.useMemo(() => {
        return FilterCategories(receipt, doFilter);
    }, [doFilter, receipt]);

    return (
        <div id={`confirmAndNextReimbursement-${ID}`} className="h-[100%] w-full inline-flex flex-col justify-start items-start gap-4 relative">
            {ConfirmAndNextAdminMenu(receipt)}
            <DateTimeInput
                type={isFormUpdating ? 'locked' : (date === null || date === '' ? 'warning' : 'edit')}
                label="Date / Time"
                receipt={mergedData}
                onUpdate={(newDate) => {
                    if (GlobalVariables.allowReceiptChanges && mergedData.transaction_date !== newDate) {
                        console.log(`transaction_date CHANGES2: |${mergedData.transaction_date}|${newDate}|`);
                        ReduxUpdater.receiptChanges = {
                            ...receiptChanges,
                            id: receipt.id,
                            message_id: receipt.message_id,
                            transaction_date: newDate,
                            transaction_time: null,
                        };
                    }
                }}
            />
            <TextInput
                idx={`merchant-${ID}`}
                type={isFormUpdating ? 'locked' : (![null, ''].includes(mergedData.vendor_name) ? 'edit' : 'empty')}
                label="Merchant"
                value={mergedData.vendor_name ?? ''}
                onChange={(newValue) => {
                    if (GlobalVariables.allowReceiptChanges && receipt.vendor_name !== newValue) {
                        console.log(`vendor_name CHANGES2: |${receipt.vendor_name}|${newValue}|`);
                        ReduxUpdater.receiptChanges = {
                            ...receiptChanges,
                            id: receipt.id,
                            message_id: receipt.message_id,
                            vendor_name: newValue,
                        };
                    }
                }}
            />
            <CurrencyInput
                idx={`amount-${ID}`}
                type={isFormUpdating ? 'locked' : (mergedData.amount !== '' && mergedData.currency !== '' && mergedData.currency !== null  ? 'edit' : 'empty')}
                label="Amount"
                amount={mergedData.amount ?? ''}
                currency={mergedData.currency ?? ''}
                onChange={(amount, currency) => {
                    if (GlobalVariables.allowReceiptChanges && (receipt.amount !== amount || receipt.currency !== currency)) {
                        console.log(`amount CHANGES2: |${receipt.amount}|${amount}|`);
                        console.log(`currency CHANGES2: |${receipt.currency}|${currency}|`);
                        ReduxUpdater.receiptChanges = {
                            ...receiptChanges,
                            id: receipt.id,
                            message_id: receipt.message_id,
                            amount: amount,
                            currency: currency,
                        };
                    }
                }}
            />
            <div className="self-stretch flex flex-col justify-center gap-2 items-start">
                <div className="sm:inline-flex sm:items-center space-y-2 sm:space-y-0 sm:space-x-3 w-full">
                    <div className="w-[50%] inline-flex self-stretch justify-start text-text-dark text-sm font-normal font-['Inter'] leading-tight">Receipt Status</div>
                </div>
                <div className="sm:inline-flex sm:items-center space-y-2 sm:space-y-0 sm:space-x-3 w-full">
                    <div className="relative self-stretch flex items-center outline-1 border-border-default hover:border-blue-400 rounded-lg bg-white transition-colors">
                        <div className="py-1.5 sm:py-2 px-4 w-80 bg-bg-input-locked outline-0 inline-flex justify-start items-center gap-2.5">
                            Reimbursement
                        </div>
                    </div>
                </div>
            </div>
            <TextInput
                idx={`userDescription-${ID}`}
                type={isFormUpdating ? 'locked' : 'edit'}
                label="User Description"
                value={mergedData.user_description ?? ''}
                onChange={(newValue) => {
                    if (GlobalVariables.allowReceiptChanges && (receipt.user_description ?? '') !== newValue) {
                        console.log(`user_description CHANGES2: |${receipt.user_description ?? ''}|${newValue}|`);
                        ReduxUpdater.receiptChanges = {
                            ...receiptChanges,
                            id: receipt.id,
                            message_id: receipt.message_id,
                            user_description: newValue,
                        };
                    }
                }}
            />
            <SelectInput
                idx={`category-${ID}`}
                type={isFormUpdating ? 'locked' : (mergedData.maris_category_id !== null ? 'edit' : 'empty')}
                label="Category"
                value={(mergedData.maris_category_id ?? -1).toString()}
                options={filteredCategories}
                selectClasses=''
                onChange={(newValue) => {
                    if (GlobalVariables.allowReceiptChanges && mergedData.maris_category_id !== parseInt(newValue) && !(mergedData.maris_category_id === null && newValue === '')) {
                        console.log(`maris_category_id CHANGES2: |${mergedData.maris_category_id}|${newValue}|`);
                        ReduxUpdater.receiptChanges = {
                            ...receiptChanges,
                            id: receipt.id,
                            message_id: receipt.message_id,
                            maris_category_id: parseInt(newValue),
                        };
                    }
                }}
                titleOrAction={showAll && doFilter
                    ? 
                        <button
                            className="text-text-action-button"
                            onClick={() => setDoFilter(false)}
                        >
                            Show all
                        </button> 
                    : ''
                }
            />
            {GlobalVariables.coOwners.length > 1 && <SelectInput
                idx={`coOwner-${ID}`}
                type={isFormUpdating ? 'locked' : 'edit'}
                label="Co-owner Expense Allocation"
                value={mergedData.co_owner_name ?? ''}
                options={GlobalVariables.coOwnersExtendedArray}
                onChange={(newValue) => {
                    if (GlobalVariables.allowReceiptChanges && receipt.co_owner_name !== newValue) {
                        ReduxUpdater.receiptChanges = {
                            ...receiptChanges,
                            id: receipt.id,
                            message_id: receipt.message_id,
                            co_owner_name: newValue,
                        };
                    }
                }}
            />}
            <VerticalStretch />
            <ProgressBar value={idx+1} max={max} />
            {
                isFormUpdating
                ? <NavSpinner label="Saving..." />
                : <NavButtonsGroup label={userCanReimburseReceipts ? 'Reimburse' : 'Next'} max={max} onRevert={handleRevert} onSave={handleSave} onPrev={onPrev} onConfirm={userCanReimburseReceipts ? handleConfirm : onNext} onNext={onNext} />
            }
        </div>
    );
};

export default ConfirmAndNextReimbursementForm;